import React from "react";
import PropTypes from "prop-types";
import { ArrowRight, Heart } from "lucide-react";
import Button from "./ui/Button";
import Typography from "./ui/Typography";
import PosterPlaceholder from "./ui/PosterPlaceholder";
import { cn } from "../utils/classNames";
import { handleInternalLinkClick } from "../utils/navigation";
import { getReleaseRadarLabel } from "../utils/watchlist";

const WatchlistShelf = ({ shows = [], className = "" }) => {
  if (!Array.isArray(shows) || shows.length === 0) {
    return null;
  }

  const visibleShows = shows.slice(0, 12);

  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl border border-accent-gold/50 bg-card/65 p-4 shadow-card-base",
        className
      )}
      aria-labelledby="watchlist-shelf-title"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-primary/50 bg-background text-primary">
            <Heart className="h-4 w-4 fill-current" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <Typography id="watchlist-shelf-title" variant="h4" className="text-foreground">
              For You
            </Typography>
            <Typography variant="caption" className="text-muted-foreground">
              Saved premieres and recent releases
            </Typography>
          </div>
        </div>
        <Button variant="outline" size="sm" asChild>
          <a href="/my-list" onClick={(event) => handleInternalLinkClick(event, "/my-list")}>
            <span className="flex items-center gap-2">
              Open My List
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </a>
        </Button>
      </div>

      <div className="-mx-4 overflow-x-auto px-4 pb-1">
        <div className="flex min-w-full gap-3">
          {visibleShows.map((show) => (
            <a
              key={show.id}
              href={show.slug ? `/show/${show.slug}` : "/my-list"}
              onClick={(event) => handleInternalLinkClick(event, show.slug ? `/show/${show.slug}` : "/my-list")}
              className="group flex w-[168px] flex-none gap-3 rounded-lg border border-border/70 bg-background/70 p-2 transition-colors hover:border-primary/60"
            >
              <div className="h-20 w-14 flex-none overflow-hidden rounded-md border border-accent-gold/40 bg-muted">
                {show.poster ? (
                  <img
                    src={show.poster}
                    alt=""
                    className="h-full w-full object-cover grayscale-[18%] contrast-110"
                    loading="lazy"
                  />
                ) : (
                  <PosterPlaceholder showId={show.id} className="h-full w-full bg-poster-placeholder-gradient" />
                )}
              </div>
              <div className="min-w-0 py-0.5">
                <Typography variant="body-small" className="title-link line-clamp-2 font-semibold leading-snug text-foreground">
                  {show.title || "Saved show"}
                </Typography>
                <Typography variant="caption" className="mt-1 line-clamp-2 text-muted-foreground">
                  {getReleaseRadarLabel(show.releaseDate)}
                </Typography>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

WatchlistShelf.propTypes = {
  shows: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
};

export default WatchlistShelf;
